import React, { useCallback, useMemo, useRef, useState } from 'react';
import {
  stretchBannerAdLabelStyles,
  StretchBannerImgWrapper,
  stretchBannerLinkStyles,
  StretchBannerWrapper,
} from '../styles';
import { useStretchBannerGa, useStretchBannerAutohide } from '../hooks';
import { AdLabelController } from './ad-label-controller';
import { BannerImage } from './banner-image';
import { ButtonCloseBanner } from './button-close-banner';
import { StretchProps } from '../types';
import { prepareTargetUrl } from '../../banners-carousel/utils';
import { targetBlank } from '../helpers';
import { useIntersectionObserveWithDelay } from '../../../common/hooks';
import { AdLabelModal } from './ad-label-modal';
import { AUTO_HIDE_HOUR } from 'common/constants';

export function Stretch(props: Readonly<StretchProps>) {
  const {
    onHiddenBanner,
    banners,
    width = 418,
    height = 56,
    showCloseButton,
    autohide,
    autohideViews,
    gaName,
    gaPosition,
    viewingTime,
    onImpressionContentView,
    onImpressionContentClick,
  } = props;
  const ref = useRef<HTMLDivElement | null>(null);
  const [isSentViewToGA, setIsSentViewToGA] = useState(false);
  const { handleClickToGA, handleViewToGA, handleClickButtonToGA } = useStretchBannerGa({
    gaPosition,
    gaName,
    setIsSentViewToGA,
  });
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);

  const { isAutohide } = useStretchBannerAutohide({
    bannerDuration: AUTO_HIDE_HOUR,
    autohide,
    autohideViews,
  });

  const bannerData = useMemo(() => {
    return banners.web?.content.banners[0];
  }, [banners]);

  const ord = useMemo(() => {
    return banners.web?.content.ord;
  }, [banners]);

  const { pictureUrl = '', targetUrl = '' } = bannerData || {};

  const backgroundColor = banners.colors?.color;

  const link = prepareTargetUrl(targetUrl, ord);

  const bannerContentId = banners.web?.content.id;

  const sendViewEvent = () => {
    if (!bannerContentId) return;
    if (onImpressionContentView) {
      onImpressionContentView(bannerContentId);
    }
    if (!isSentViewToGA) {
      handleViewToGA();
    }
  };

  const viewingTimeMilliSeconds = viewingTime * 1000;

  useIntersectionObserveWithDelay(ref, sendViewEvent, { threshold: 0.5 }, viewingTimeMilliSeconds);

  const handleClick = useCallback(() => {
    handleClickToGA();
    if (onImpressionContentClick && bannerContentId) {
      onImpressionContentClick(bannerContentId);
    }
  }, [handleClickToGA, onImpressionContentClick, bannerContentId]);

  if (isAutohide) return null;
  if (!backgroundColor || !ord || !bannerContentId) return null;

  return (
    <>
      <StretchBannerWrapper data-qa="stretch-banner" ref={ref} backgroundColor={backgroundColor}>
        <a
          className={stretchBannerLinkStyles}
          onClick={handleClick}
          href={link}
          target={targetBlank(targetUrl)}>
          <StretchBannerImgWrapper modelWidth={width} modelHeight={height}>
            <BannerImage {...{ pictureUrl }} />
          </StretchBannerImgWrapper>
        </a>
        <div className={stretchBannerAdLabelStyles}>
          <AdLabelController ord={ord} toggleModal={() => setIsModalOpen(prev => !prev)} />
          <ButtonCloseBanner
            showCloseButton={showCloseButton}
            onHiddenBanner={onHiddenBanner}
            backgroundColor={backgroundColor}
            handleClickButtonToGA={handleClickButtonToGA}
          />
        </div>
      </StretchBannerWrapper>
      <AdLabelModal {...{ ord }} isModalOpen={isModalOpen} close={() => setIsModalOpen(false)} />
    </>
  );
}
